import React, { useMemo } from 'react'
import { t } from 'src/translations'
import {
  FullSchedule,
  getAcceptingOrdersUntil,
  mergeSchedules
} from 'src/lib/js/schedule'
import { useRestaurant } from 'src/shared/components/common/restaurant_context/RestaurantContext'
import { getDisplayBusinessName } from 'src/shared/js/locationUtils'
import {
  useFulfillment,
  useLocationSchedules
} from 'public/components/online_ordering/FulfillmentContext'
import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { CalendarTodayIcon, ChevronRightIcon } from '@toasttab/buffet-pui-icons'
import AllHoursModal from 'public/components/default_template/footer/location/AllHoursModal'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'
import { useIsDarkMode } from 'public/components/default_template/meta/useTheme'
import classnames from 'classnames'

const ClosingTimeAlert = () => {
  const { restaurant, ooRestaurant, selectedLocation } = useRestaurant()
  const { fulfillmentData } = useFulfillment()
  const { ooAlertClosingSoon } = useFlags()
  const darkMode = useIsDarkMode()
  const locationSchedules = useLocationSchedules()
  const locationSchedule = locationSchedules[selectedLocation.externalId]
  const diningOptionBehavior = useMemo(
    () =>
      fulfillmentData?.cartFulfillmentData?.diningOptionBehavior ??
      DiningOptionBehavior.TakeOut,
    [fulfillmentData?.cartFulfillmentData?.diningOptionBehavior]
  )

  const rxSchedule = useMemo(() => {
    const schedules = ooRestaurant?.schedule.upcomingSchedules
    // Look for the schedule for the current diningOptionBehavior, but fall back on takeout if not found.
    let schedule = schedules?.find((s) => s.behavior === diningOptionBehavior)
    if (!schedule) {
      schedule = schedules?.find(
        (s) => s.behavior === DiningOptionBehavior.TakeOut
      )
    }
    return schedule
  }, [ooRestaurant?.schedule.upcomingSchedules, diningOptionBehavior])

  const timezoneId = useMemo(
    () =>
      ooRestaurant?.timeZoneId ??
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    [ooRestaurant?.timeZoneId]
  )

  const mergedSchedules: FullSchedule | undefined = useMemo(
    () =>
      mergeSchedules(
        selectedLocation,
        locationSchedule,
        !!restaurant.config.isOnlineOrderingOnly,
        diningOptionBehavior
      ),
    [
      selectedLocation,
      locationSchedule,
      restaurant.config.isOnlineOrderingOnly,
      diningOptionBehavior
    ]
  )

  if (!ooRestaurant || !ooAlertClosingSoon) {
    return null
  }

  const acceptingOrdersUntil = getAcceptingOrdersUntil(
    rxSchedule,
    ooRestaurant.schedule.lastOrderConfiguration,
    ooRestaurant.minimumTakeoutTime,
    timezoneId,
    ooRestaurant.i18n.locale
  )

  // If the Rx is closed, don't render the alert
  if (!acceptingOrdersUntil) {
    return null
  }

  return (
    <AllHoursModal
      restaurantName={
        getDisplayBusinessName(ooRestaurant.name, selectedLocation?.name) ??
        ooRestaurant.name
      }
      isLocationOpen={true}
      schedule={mergedSchedules}
      timezoneId={timezoneId}
    >
      {(open) => {
        // Modal Target
        return (
          <button
            className={classnames('closingTimeAlert', { darkMode })}
            onClick={open}
          >
            <div className='content'>
              <CalendarTodayIcon size='xs' accessibility='decorative' />
              <div className='textContent'>
                {t('fulfillment-panel.closing-time-alert', {
                  acceptingOrdersUntil: acceptingOrdersUntil
                })}
              </div>
            </div>
            <ChevronRightIcon
              size='xs'
              accessibility='semantic'
              aria-label={t('fulfillment-panel.view-restaurant-hours')}
              className='hidden-md-up'
            />
          </button>
        )
      }}
    </AllHoursModal>
  )
}

export default ClosingTimeAlert
